import { useCallback } from 'react'
import { useApolloClient } from '@apollo/client'
import _ from 'lodash'
import { GET_PATIENT_HISTORY, GET_PATIENT_HISTORY_VALUES } from 'src/legacy/lib/graphql/queries'
import { GetPatientHistoryQuery, GetPatientHistoryValuesQuery } from 'src/legacy/lib/graphql/queries.generated'
import Big from 'big.js'

import {
  useAddFinancialEligibilityPatientHistoryMutation,
  useAddPatientHistoryMutation,
  GetPatientHistoryAndProgramStartDateDocument,
} from 'src/participantHistory/patientHistoryQueries.generated'

import {
  FinancialEligibilityInsuranceSubscriberInput,
  PatientHistoryType as GraphQLPatientHistoryType,
} from 'src/graphql.generated'
import type { FinancialEligibilityHealthPlanSchema } from 'src/participantHistory/financialEligibility/FinancialEligibilityHealthPlanFields'
import type { PatientHistoryEntry } from 'src/legacy/components/ParticipantRecords/ParticipantHistory/types'
import { GetPatientHistoryTypesDocument } from 'src/participantHistoryForm/getPatientHistoryTypes.generated'
import { GetFullPatientDocument } from 'src/participant/patientQueries.generated'
import { encodeObjectForWaf } from 'src/lib/wafEncoding'
import { INTAKE_NOTE_TYPES } from 'src/participantHistory/constants'
import { GetParticipantVideoCallBookingInformationDocument } from 'src/videoCall/VideoCallBookingHeader/videoCallBookingHeaderQueries.generated'
import { GetUncancelledInProgramCallsDocument } from 'src/participantHistoryForm/form/checkinCall/getUncancelledInProgramCalls.generated'
import { FetchParticipantsForHomeViewDocument } from 'src/participantsHome/searchParticipants.generated'
import { forgetSubmittedNoteDraft, releaseNoteDraftHold, settleNoteDraft } from 'src/noteDraft/useNoteDraft'

type FormPatientHistoryType = PatientHistoryEntry['type']

type FinancialEligibilityPatientHistoryEntry = PatientHistoryEntry & {
  value: FinancialEligibilityHealthPlanSchema
}

// FIXME: This is hack to remap the form type FINANCIAL_ELIGIBILITY_HEALTH_PLAN before saving the form
// We have a plan to merge FINANCIAL_ELIGIBILITY_* type to one in the near future
const getGraphQLPatientHistoryTypeFromFormPatientHistoryType = (formPatientHistoryType: FormPatientHistoryType) =>
  formPatientHistoryType === 'FINANCIAL_ELIGIBILITY_HEALTH_PLAN'
    ? GraphQLPatientHistoryType.FinancialEligibilityConfirmed
    : formPatientHistoryType

// Notes that can be the submitted note of a completed call for the call follow-up
// indicator (see PatientIndicators.completedCallAwaitingNote in the server): intake notes
// are matched by type, other call notes by their link to the call.
const CALL_FOLLOW_UP_CLEARING_TYPES: FormPatientHistoryType[] = [
  ...INTAKE_NOTE_TYPES,
  GraphQLPatientHistoryType.MissedIntakeCall,
]

const clearsCallFollowUpIndicator = (type: FormPatientHistoryType, hasLinkedCallID: boolean) =>
  hasLinkedCallID || CALL_FOLLOW_UP_CLEARING_TYPES.includes(type)

const useAddCommonPatientHistoryEntry = () => {
  const [addPatientHistory] = useAddPatientHistoryMutation()
  return useCallback(
    (patientHistoryEntry: PatientHistoryEntry) => {
      const { references, value, ...rest } = patientHistoryEntry
      const hasLinkedCallID = typeof references?.callID === 'string' && references.callID.length > 0

      return addPatientHistory({
        variables: {
          ...rest,
          references,
          // Encode the value to bypass Cloud Armor WAF rules that may block
          // clinical notes containing patterns like "<script>", "eval()", etc.
          value: encodeObjectForWaf(value as Record<string, unknown>),
          type: getGraphQLPatientHistoryTypeFromFormPatientHistoryType(patientHistoryEntry.type),
          fileIDs: patientHistoryEntry.fileIDs,
        },
        refetchQueries: [
          {
            query: GetPatientHistoryTypesDocument,
            variables: {
              uid: patientHistoryEntry.uid,
            },
          },
          ...(hasLinkedCallID
            ? [
                {
                  query: GetParticipantVideoCallBookingInformationDocument,
                  variables: { uid: patientHistoryEntry.uid },
                },
                {
                  query: GetUncancelledInProgramCallsDocument,
                  variables: { uid: patientHistoryEntry.uid },
                },
              ]
            : []),
          // Only the server knows whether another completed call is still awaiting a note,
          // so let it recalculate the patient list's call follow-up indicator.
          ...(clearsCallFollowUpIndicator(patientHistoryEntry.type, hasLinkedCallID)
            ? [FetchParticipantsForHomeViewDocument]
            : []),
          // The toast and list badge read monthlySummaryNotes on this patient. The server
          // decides whether they should stay up, so refetch the patient they share.
          ...(patientHistoryEntry.type === GraphQLPatientHistoryType.MonthlySummaryNote
            ? [
                {
                  query: GetFullPatientDocument,
                  variables: { uid: patientHistoryEntry.uid },
                },
              ]
            : []),
        ],
        update: (cache, result) => {
          const { uid } = patientHistoryEntry
          const patientHistory = result.data?.addPatientHistory
          const getPatientHistory = cache.readQuery<GetPatientHistoryQuery>({
            query: GET_PATIENT_HISTORY,
            variables: { uid },
          })
          if (getPatientHistory) {
            cache.writeQuery({
              data: {
                patientHistory: _.orderBy([patientHistory, ...getPatientHistory.patientHistory], 'date', 'desc'),
              },
              query: GET_PATIENT_HISTORY,
              variables: { uid },
            })
          }
          const getPatientHistoryValues = cache.readQuery<GetPatientHistoryValuesQuery>({
            query: GET_PATIENT_HISTORY_VALUES,
            variables: {
              uid,
              type: patientHistory?.type,
            },
          })
          if (getPatientHistoryValues) {
            cache.writeQuery({
              data: {
                patientHistory: _.orderBy([...getPatientHistoryValues.patientHistory, patientHistory], 'date', 'asc'),
              },
              query: GET_PATIENT_HISTORY_VALUES,
              variables: {
                uid,
                type: patientHistory?.type,
              },
            })
          }
        },
      })
    },
    [addPatientHistory]
  )
}

const insuranceSubscriberToGraphQL = (
  insuranceSubscriber: FinancialEligibilityPatientHistoryEntry['value']['insuranceSubscriber']
): FinancialEligibilityInsuranceSubscriberInput | null => {
  if (insuranceSubscriber?.insuranceSubscriberProvided) {
    const { firstname, lastname, memberID } = insuranceSubscriber
    // Typing the union type in Yup doesn't seem to be possible, so a
    // check here that should never throw an error.
    if (!firstname || !lastname || !memberID) throw new Error('Invalid insurance details')
    return {
      ..._.omit(insuranceSubscriber, 'insuranceSubscriberProvided'),
      firstname,
      lastname,
      memberID,
    } as FinancialEligibilityInsuranceSubscriberInput
  }

  return null
}

const useAddFinancialEligibilityPatientHistoryEntry = () => {
  const [adddFinancialEligibilityPatientHistoryMutation] = useAddFinancialEligibilityPatientHistoryMutation({
    refetchQueries: [GetPatientHistoryAndProgramStartDateDocument, GetFullPatientDocument],
  })

  return useCallback(
    (patientHistoryEntry: FinancialEligibilityPatientHistoryEntry) => {
      const { uid, date, value } = patientHistoryEntry
      const { description, insuranceSubscriber, costEstimate } = value
      const { intakeCostEstimate, month1CostEstimate, month2CostEstimate, month3CostEstimate } = costEstimate

      const costEstimateInCents =
        _.isNumber(intakeCostEstimate) &&
        _.isNumber(month1CostEstimate) &&
        _.isNumber(month2CostEstimate) &&
        _.isNumber(month3CostEstimate)
          ? {
              intakeCostEstimateInCents: new Big(intakeCostEstimate).times(100).toNumber(),
              month1CostEstimateInCents: new Big(month1CostEstimate).times(100).toNumber(),
              month2CostEstimateInCents: new Big(month2CostEstimate).times(100).toNumber(),
              month3CostEstimateInCents: new Big(month3CostEstimate).times(100).toNumber(),
            }
          : null

      return adddFinancialEligibilityPatientHistoryMutation({
        variables: {
          uid,
          date,
          description,
          insuranceSubscriber: insuranceSubscriberToGraphQL(insuranceSubscriber),
          costEstimate: costEstimateInCents,
        },
      })
    },
    [adddFinancialEligibilityPatientHistoryMutation]
  )
}

export default function useAddPatientHistoryEntry() {
  const client = useApolloClient()
  const addCommonPatientHistoryEntry = useAddCommonPatientHistoryEntry()
  const addFinancialEligibilityPatientHistoryEntry = useAddFinancialEligibilityPatientHistoryEntry()

  return useCallback(
    async (patientHistoryEntry: PatientHistoryEntry) => {
      await settleNoteDraft({ uid: patientHistoryEntry.uid, type: patientHistoryEntry.type })
      try {
        const result =
          patientHistoryEntry.type === 'FINANCIAL_ELIGIBILITY_HEALTH_PLAN'
            ? await addFinancialEligibilityPatientHistoryEntry(
                patientHistoryEntry as FinancialEligibilityPatientHistoryEntry
              )
            : await addCommonPatientHistoryEntry(patientHistoryEntry)
        forgetSubmittedNoteDraft(client.cache, {
          uid: patientHistoryEntry.uid,
          type: patientHistoryEntry.type,
        })
        return result
      } finally {
        releaseNoteDraftHold({ uid: patientHistoryEntry.uid, type: patientHistoryEntry.type })
      }
    },
    [addCommonPatientHistoryEntry, addFinancialEligibilityPatientHistoryEntry, client]
  )
}
